/**
 * Price & packaging in the V3 editor — the pure part.
 *
 * Each variant stores its own `commerce.price / pack / sample`; the API also
 * hands back the read-only `pricing` it resolved for shoppers (manual > shop >
 * import > page > attributes). The card shows the vendor's own values as
 * editable, and a resolved value from elsewhere read-only with where it came
 * from, until the vendor chooses to set their own.
 *
 * Kept free of React so the rules are tested alone.
 */
import type {
  ProductCommerce,
  ProductPrice,
  ProductPack,
  ProductSample,
  ProductPricing,
  ResolvedPrice,
  ResolvedPack,
  PriceSource,
  PriceUnit,
} from '../../../../common/models/product-v2.model';
import {
  isUsablePack,
  isUsablePrice,
  normalizeCurrency,
  roundMoney,
} from '../../../../common/utils/product-pricing';
import type { VariantV3 } from '../types';
import { formatAxisValueForDisplay } from '../types';

export type MoneyKey = 'price' | 'pack' | 'sample';
export const MONEY_KEYS: MoneyKey[] = ['price', 'pack', 'sample'];
export type CommerceMoney = Partial<Pick<ProductCommerce, MoneyKey>>;

const isManual = (source: PriceSource | undefined) => (source ?? 'manual') === 'manual';

// ─────────────────────────────────────────────────────────────────────────────
// Provenance
// ─────────────────────────────────────────────────────────────────────────────

/** A price as the card shows it, whichever side it came from. */
export interface ShownPrice {
  amount: number;
  currency: string;
  unit?: PriceUnit;
  compareAt?: number;
}

export interface PriceView {
  /**
   * manual — the vendor's own, editable.
   * auto   — from the shop, a page read, an import or the attributes; read-only until the vendor takes it over.
   * none   — nothing says what it costs.
   */
  state: 'manual' | 'auto' | 'none';
  price: ShownPrice | null;
  source: PriceSource | null;
  /** Nobody said what the price is per. */
  unitAssumed: boolean;
  /** For a manual price: the source the vendor can go back to, when the load told us one. */
  fallbackSource: PriceSource | null;
}

/**
 * How the card presents a variant's price: its stored `commerce.price` and the
 * `pricing.price` the API resolved when it was loaded.
 *
 * A manual price is the vendor's, whatever else exists. Otherwise the resolved
 * one is shown with its source; a stored non-manual price (an import) stands in
 * when the load carried no `pricing`. A resolved price whose source is
 * 'manual' while nothing manual is stored is one the vendor just removed.
 */
export function describePrice(
  stored: ProductPrice | null | undefined,
  resolved: ResolvedPrice | null | undefined,
): PriceView {
  if (isUsablePrice(stored) && isManual(stored.source)) {
    return {
      state: 'manual',
      price: shownPrice(stored),
      source: 'manual',
      unitAssumed: !stored.unit,
      fallbackSource: resolved && !isManual(resolved.source) ? resolved.source : null,
    };
  }
  if (resolved && !isManual(resolved.source) && resolved.amount > 0 && resolved.currency) {
    return {
      state: 'auto',
      price: shownPrice(resolved),
      source: resolved.source,
      unitAssumed: !!resolved.unitAssumed,
      fallbackSource: null,
    };
  }
  if (isUsablePrice(stored)) {
    return { state: 'auto', price: shownPrice(stored), source: stored.source, unitAssumed: !stored.unit, fallbackSource: null };
  }
  return { state: 'none', price: null, source: null, unitAssumed: false, fallbackSource: null };
}

function shownPrice(p: ProductPrice | ResolvedPrice): ShownPrice {
  return {
    amount: p.amount,
    currency: normalizeCurrency(p.currency) ?? p.currency,
    ...(p.unit ? { unit: p.unit } : {}),
    ...(p.compareAt && p.compareAt > p.amount ? { compareAt: p.compareAt } : {}),
  };
}

export interface PackView {
  state: 'manual' | 'auto' | 'none';
  pack: { m2PerBox?: number; piecesPerBox?: number } | null;
  source: PriceSource | null;
  fallbackSource: PriceSource | null;
}

/** The same rules for the packaging. A pack stored without a source is the vendor's. */
export function describePack(
  stored: ProductPack | null | undefined,
  resolved: ResolvedPack | null | undefined,
): PackView {
  if (isUsablePack(stored) && isManual(stored.source)) {
    return {
      state: 'manual',
      pack: packValues(stored),
      source: 'manual',
      fallbackSource: resolved && !isManual(resolved.source) ? resolved.source : null,
    };
  }
  if (isUsablePack(resolved) && !isManual(resolved.source)) {
    return { state: 'auto', pack: packValues(resolved), source: resolved.source, fallbackSource: null };
  }
  if (isUsablePack(stored)) {
    return { state: 'auto', pack: packValues(stored), source: stored.source ?? null, fallbackSource: null };
  }
  return { state: 'none', pack: null, source: null, fallbackSource: null };
}

const packValues = (p: ProductPack) => ({
  ...((p.m2PerBox ?? 0) > 0 ? { m2PerBox: p.m2PerBox } : {}),
  ...((p.piecesPerBox ?? 0) > 0 ? { piecesPerBox: p.piecesPerBox } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// What a variant was loaded with, and what "go back" restores
// ─────────────────────────────────────────────────────────────────────────────

/** The commerce a variant was loaded with, read from its dirty-check snapshot. */
export function loadedCommerceOf(variant: Pick<VariantV3, '_originalSnapshot'>): ProductCommerce | undefined {
  if (!variant._originalSnapshot) return undefined;
  try {
    return JSON.parse(variant._originalSnapshot)?.commerce ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * What removing the vendor's own price goes back to: the stored non-manual
 * price it replaced (an import), else nothing — the API then resolves the
 * shop's or the page's again. Never a blanket null, which would wipe an import.
 */
export function priceToRestore(loaded: ProductCommerce | undefined): ProductPrice | null {
  const price = loaded?.price;
  return isUsablePrice(price) && !isManual(price.source) ? price : null;
}

/** The same for the packaging. */
export function packToRestore(loaded: ProductCommerce | undefined): ProductPack | null {
  const pack = loaded?.pack;
  return isUsablePack(pack) && !!pack.source && !isManual(pack.source) ? pack : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Building the vendor's own values
// ─────────────────────────────────────────────────────────────────────────────

/** A manual price, or null when the amount or the currency is missing (never a zero). */
export function manualPrice(input: {
  amount?: number;
  currency?: string;
  unit?: PriceUnit;
  compareAt?: number;
}): ProductPrice | null {
  const currency = normalizeCurrency(input.currency);
  if (!input.amount || input.amount <= 0 || !currency) return null;
  const amount = roundMoney(input.amount);
  return {
    amount,
    currency,
    ...(input.unit ? { unit: input.unit } : {}),
    ...(input.compareAt && input.compareAt > 0 ? { compareAt: roundMoney(input.compareAt) } : {}),
    source: 'manual',
  };
}

/** A manual pack, or null when it says nothing. */
export function manualPack(input: { m2PerBox?: number; piecesPerBox?: number }): ProductPack | null {
  const m2 = input.m2PerBox && input.m2PerBox > 0 ? Math.round(input.m2PerBox * 10000) / 10000 : undefined;
  const pieces = input.piecesPerBox && input.piecesPerBox > 0 ? Math.round(input.piecesPerBox) : undefined;
  if (!m2 && !pieces) return null;
  return { ...(m2 ? { m2PerBox: m2 } : {}), ...(pieces ? { piecesPerBox: pieces } : {}), source: 'manual' };
}

// ─────────────────────────────────────────────────────────────────────────────
// Variants
// ─────────────────────────────────────────────────────────────────────────────

/** One line per distinct price (and its provenance) so the card knows whether variants differ. */
export function priceSignature(commerce: ProductCommerce | undefined, pricing: ProductPricing | undefined): string {
  const view = describePrice(commerce?.price, pricing?.price);
  return view.price
    ? `${view.state}:${view.price.amount}|${view.price.currency}|${view.price.unit ?? ''}|${view.price.compareAt ?? ''}`
    : 'none';
}

export function packSignature(commerce: ProductCommerce | undefined, pricing: ProductPricing | undefined): string {
  const view = describePack(commerce?.pack, pricing?.pack);
  return view.pack ? `${view.state}:${view.pack.m2PerBox ?? ''}|${view.pack.piecesPerBox ?? ''}` : 'none';
}

/** Whether the selected variants carry different prices or packaging. */
export function variantsDiffer(variants: Array<Pick<VariantV3, 'commerce' | 'pricing' | 'isSelected'>>): boolean {
  const selected = variants.filter((v) => v.isSelected);
  if (selected.length < 2) return false;
  const prices = new Set(selected.map((v) => priceSignature(v.commerce, v.pricing)));
  const packs = new Set(selected.map((v) => packSignature(v.commerce, v.pricing)));
  return prices.size > 1 || packs.size > 1;
}

/** "Oak · 20 × 120 cm · herringbone", else the SKU, else "#2". */
export function variantLabel(variant: Pick<VariantV3, 'axes' | 'sku'>, index: number): string {
  const parts = Object.values(variant.axes ?? {})
    .map((value) => formatAxisValueForDisplay(value))
    .filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return variant.sku || `#${index + 1}`;
}

/**
 * The commerce a freshly generated variant starts with: the product's links,
 * and its price, packaging and sample so a new colour is not left unpriced.
 */
export function inheritedCommerce(product: ProductCommerce | undefined): ProductCommerce {
  const commerce: ProductCommerce = { link: product?.link, addToCartLink: product?.addToCartLink };
  for (const key of MONEY_KEYS) {
    if (product?.[key] !== undefined) (commerce as any)[key] = product[key];
  }
  return commerce;
}

// ─────────────────────────────────────────────────────────────────────────────
// Payload
// ─────────────────────────────────────────────────────────────────────────────

/** A stored price as the API accepts it, or null when it is not a price. */
function cleanPrice(price: ProductPrice): ProductPrice | null {
  if (!isUsablePrice(price)) return null;
  return {
    amount: roundMoney(price.amount),
    currency: normalizeCurrency(price.currency)!,
    ...(price.unit ? { unit: price.unit } : {}),
    ...(price.compareAt && price.compareAt > price.amount ? { compareAt: roundMoney(price.compareAt) } : {}),
    source: price.source ?? 'manual',
    ...(price.updatedAt ? { updatedAt: price.updatedAt } : {}),
  };
}

function cleanPack(pack: ProductPack): ProductPack | null {
  if (!isUsablePack(pack)) return null;
  return { ...packValues(pack), ...(pack.source ? { source: pack.source } : {}) };
}

function cleanSample(sample: ProductSample): ProductSample | null {
  if (!sample || typeof sample.available !== 'boolean') return null;
  return {
    available: sample.available,
    ...(typeof sample.price === 'number' && sample.price >= 0 ? { price: roundMoney(sample.price) } : {}),
    ...(normalizeCurrency(sample.currency) ? { currency: normalizeCurrency(sample.currency) } : {}),
  };
}

/**
 * price / pack / sample for a variant's payload.
 *
 * The variant's own value first — `null` is sent as null, which clears it. A
 * new variant without one takes the product's. An existing variant without
 * one sends nothing for that key, which the API reads as "keep what is
 * stored". The read-only `pricing` is never part of it.
 */
export function commerceMoneyForPayload(
  variantCommerce: ProductCommerce | undefined,
  productCommerce: ProductCommerce | undefined,
  isNew: boolean,
): CommerceMoney {
  const out: CommerceMoney = {};
  const pick = (key: MoneyKey) => {
    if (variantCommerce && variantCommerce[key] !== undefined) return variantCommerce[key];
    if (isNew && productCommerce && productCommerce[key] !== undefined) return productCommerce[key];
    return undefined;
  };
  const price = pick('price');
  if (price !== undefined) out.price = price ? cleanPrice(price as ProductPrice) : null;
  const pack = pick('pack');
  if (pack !== undefined) out.pack = pack ? cleanPack(pack as ProductPack) : null;
  const sample = pick('sample');
  if (sample !== undefined) out.sample = sample ? cleanSample(sample as ProductSample) : null;
  return out;
}
